import isApprovalResponse from 'owa-mail-compose-store/lib/utils/isApprovalResponse';
import { getSignatureSections } from '../sections';
import getAttachFileSections, {
    AttachmentFlyoutSections,
} from 'owa-mail-compose-view-shared/lib/utils/getAttachFileSections';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { constructKeytip, createAppFlyoutAnchor, getKeytipString } from 'owa-command-ribbon';
import { createMailAppButton, createMailAppFlyoutAnchor } from '../../util/createMailRibbonButton';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import { getControlInfo, getIsButtonDisabled } from 'owa-mail-ribbon-utils';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import { getTableControl as getTableControlFromActionControls } from 'owa-compose-actioncontrols';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyGetAttachmentSuggestionsMenuItems } from 'owa-attachment-suggestions';
import { ActionControlId, MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import type { IKeytipProps } from '@fluentui/react/lib/Keytip';
import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import type AttachmentMultiPicker from 'owa-attachment-filepicker/lib/types/AttachmentMultiPicker';
import type { AttachmentSuggestionsMenuItems } from 'owa-attachment-suggestions';
import { onShowLinkDialogClicked, onEmojisButtonClicked } from '../../util/commandActions';

/**
 * This file is for shared components in compose scenarios. Meaning, any tab or control that only shows up
 * when a user is composing. In this case, Insert tab and Message tab are good examples.
 *
 * For controls that show up in read-only scenarios, please use readSharedControlDefinitions.ts.
 */
export function getTableControl(
    popoutComposeViewState: ComposeViewState | undefined
): RibbonControlProps {
    popoutComposeViewState = popoutComposeViewState ?? getComposeViewState();
    const controlInfo = getControlInfo(ActionControlId.InsertTable);
    const id: string | number = getComposeRibbonId(
        ActionControlId.InsertTable,
        popoutComposeViewState
    );
    const keytip: IKeytipProps | undefined = isFeatureEnabled('mon-ribbon-keytips')
        ? constructKeytip(
              [getKeytipString(MailRibbonTabId.Tab_Insert)],
              getKeytipString(ActionControlId.InsertTable),
              true /* hasMenu */
          )
        : undefined;
    const isPopout: boolean = popoutComposeViewState != undefined;
    const isButtonDisabled: boolean = getIsButtonDisabled(id, isPopout);

    return getTableControlFromActionControls(
        id,
        popoutComposeViewState,
        controlInfo.tooltip,
        keytip,
        isButtonDisabled
    );
}

export const getAttachFileControl = computedFn(
    (
        attachmentPickers: AttachmentMultiPicker,
        popoutComposeViewState: ComposeViewState | undefined,
        tab: MailRibbonTabId
    ): RibbonControlProps => {
        const suggestionsGetter:
            | AttachmentSuggestionsMenuItems
            | undefined = lazyGetAttachmentSuggestionsMenuItems.tryImportForRender()?.(
            true /* isMail */
        );
        const controlInfo = getControlInfo(MailRibbonControlId.AttachFile);
        const attachmentSections: AttachmentFlyoutSections = getAttachFileSections(
            attachmentPickers,
            tab,
            suggestionsGetter,
            popoutComposeViewState
        );
        const viewState: ComposeViewState | undefined =
            popoutComposeViewState ?? getComposeViewState();
        return createMailAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.AttachFile, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            { sections: attachmentSections.menuSections },
            constructKeytip(
                [getKeytipString(tab)],
                getKeytipString(MailRibbonControlId.AttachFile),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColors*/,
            undefined /*Styles*/,
            {
                onExecute: attachmentSections.onMenuOpened,
                onAfterMenuDismiss: attachmentSections.onMenuDismissed,
                disabled: !viewState || isApprovalResponse(viewState),
            }
        );
    }
);

export const getAttachSigControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        tab: MailRibbonTabId
    ): RibbonControlProps => {
        const signatureSections: MenuSection[] = getSignatureSections(tab, popoutComposeViewState);
        const controlInfo = getControlInfo(MailRibbonControlId.AttachSig);
        return createAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.AttachSig, popoutComposeViewState),
            controlInfo.text,
            {
                sections: signatureSections,
            },
            constructKeytip(
                [getKeytipString(tab)],
                getKeytipString(MailRibbonControlId.AttachSig),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColors*/,
            getDefaultRibbonStyles(),
            {
                customTooltip: controlInfo.tooltip,
            }
        );
    }
);

export const getEmojisControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Emojis);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.Emojis, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                const viewState: ComposeViewState | undefined =
                    popoutComposeViewState ?? getComposeViewState();
                onEmojisButtonClicked(viewState, targetWindow);
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Insert)],
                getKeytipString(MailRibbonControlId.Emojis)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    }
);

export const getLinkControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ShowLinkDialog);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.ShowLinkDialog, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                const viewState: ComposeViewState | undefined =
                    popoutComposeViewState ?? getComposeViewState();
                if (viewState) {
                    onShowLinkDialogClicked(viewState, targetWindow);
                }
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Insert)],
                getKeytipString(MailRibbonControlId.ShowLinkDialog)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    }
);
